import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, AlertTriangle, Upload, CheckCircle2 } from "lucide-react";
import { getDreFechamento, getRoleFromToken, importarDreFechamento, putDreFechamentoRealizadoAte } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import DreFechamentoView from "../../components/gestaoOrcamentaria/DreFechamentoView";
import { MESES_ABREV } from "../../utils/orcamento";

// DRE 2026: DRE consolidada vinda do CSV "DRE comparativa" (tabela realizado_dre_mensal) —
// realizado nos meses fechados, orçado nos demais (ver backend/services/dre_fechamento_svc.py).
// A cada fechamento o admin reimporta o CSV e avança o "Realizado até". A comparação com o
// planejado de 2027 fica no Acumulado (YTD) da DRE Gerencial.

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  bgActive: isLight ? "#F1E9FE" : "#241A3D",
  categoriaBg: isLight ? "#EDE0FB" : "#251A45",
  subcategoriaBg: isLight ? "#F5F0FC" : "#1E1435",
  margemBg: isLight ? "#FEF3E2" : "#2E2210",
  orcadoBg: isLight ? "#FFFBEB" : "#2A2210",
  success: "#10B981",
  warning: "#D97706",
  danger: "#EF4444",
});

const ANO = 2026;
const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
  cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function PainelImportacao({ ano, dados, onAtualizado, C }) {
  const inputRef = useRef(null);
  const [realizadoAte, setRealizadoAte] = useState(dados?.realizadoAte ?? 0);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => { setRealizadoAte(dados?.realizadoAte ?? 0); }, [dados?.realizadoAte]);

  const executar = async (acao) => {
    setEnviando(true);
    setErro("");
    setResultado(null);
    try {
      setResultado(await acao());
      onAtualizado();
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const importar = (arquivo) => {
    if (!arquivo) return;
    executar(() => importarDreFechamento(ano, realizadoAte, arquivo));
    if (inputRef.current) inputRef.current.value = "";
  };

  const btn = (primario) => ({
    display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, padding: "7px 12px",
    fontSize: 12, fontWeight: 600, cursor: enviando ? "wait" : "pointer",
    background: primario ? C.brand : "transparent", color: primario ? "#fff" : C.brand,
    border: `1px solid ${C.brand}`, opacity: enviando ? 0.6 : 1,
  });

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Atualizar fechamento
        </span>
        <label style={{ fontSize: 12, color: C.text, display: "flex", alignItems: "center", gap: 6 }}>
          Realizado até
          <select
            value={realizadoAte}
            onChange={(e) => setRealizadoAte(parseInt(e.target.value, 10))}
            style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, background: C.card, color: C.text }}
          >
            <option value={0}>Nenhum mês</option>
            {MESES_ABREV.map((m, i) => <option key={m} value={i + 1}>{m}/{ano}</option>)}
          </select>
        </label>
        <button type="button" disabled={enviando} style={btn(true)} onClick={() => inputRef.current?.click()}>
          <Upload size={13} /> Importar CSV
        </button>
        <button
          type="button"
          disabled={enviando || realizadoAte === dados?.realizadoAte}
          style={btn(false)}
          title="Só muda quais meses contam como realizado, sem reimportar valores"
          onClick={() => executar(() => putDreFechamentoRealizadoAte(ano, realizadoAte))}
        >
          <CheckCircle2 size={13} /> Aplicar só o status
        </button>
        <input ref={inputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={(e) => importar(e.target.files?.[0])} />
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
        CSV no formato "DRE comparativa" (DESCRIÇÃO, VALOR, MÊS, ANO, Conta). Os meses presentes no arquivo são substituídos;
        meses até o selecionado ficam como <b>realizado</b>, os seguintes como <b>orçado</b>.
      </div>
      {erro && <div style={{ marginTop: 8, fontSize: 12, color: C.danger }}>{erro}</div>}
      {resultado && (
        <div style={{ marginTop: 8, fontSize: 12, color: C.text }}>
          <span style={{ color: C.success, fontWeight: 700 }}>Atualizado.</span>{" "}
          {resultado.mesesImportados && `Meses importados: ${resultado.mesesImportados.map((m) => MESES_ABREV[m - 1]).join(", ")}. `}
          Realizado até {resultado.realizadoAte ? `${MESES_ABREV[resultado.realizadoAte - 1]}/${ano}` : "nenhum mês"}.
          {resultado.avisos?.length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, color: C.warning }}>
              {resultado.avisos.map((a) => <li key={a}>{a}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function DreFechamento() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";

  const ano = ANO;
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const carregar = useCallback(() => {
    let cancelado = false;
    setLoading(true);
    setErro("");
    getDreFechamento(ano)
      .then((resposta) => { if (!cancelado) setDados(resposta); })
      .catch((e) => { if (!cancelado) setErro(e.message); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [ano]);

  useEffect(() => carregar(), [carregar]);

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· DRE {ano}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={{ ...headerBtnStyle, fontSize: 13, padding: "7px 14px" }}>
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          {isAdmin && dados && <PainelImportacao ano={ano} dados={dados} onAtualizado={carregar} C={C} />}

          {loading && !dados ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
          ) : (
            dados && <DreFechamentoView data={dados} C={C} carregando={loading} />
          )}
        </main>
      </div>
    </div>
  );
}
